import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import SuperviseurLayout from "../../components/layouts/SuperviseurLayout";
import api from "../../services/api";

const StatCard = ({ label, value, color, bg }) => (
  <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "12px", padding: "1rem" }}>
    <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px" }}>{label}</div>
    <div style={{ fontSize: "22px", fontWeight: "500", color: color || "#111827" }}>{value}</div>
  </div>
);

const DashboardSuperviseur = () => {
  const { utilisateur } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get(`/superviseurs/${utilisateur.id}/dashboard`);
        setData(res.data);
      } catch (err) {
        setError("Impossible de charger le dashboard");
      } finally {
        setLoading(false);
      }
    };
    if (utilisateur?.id) fetch();
  }, [utilisateur]);

  const today = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  if (loading) return (
    <SuperviseurLayout>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
        <div style={{ width: "36px", height: "36px", border: "3px solid #E1F5EE", borderTop: "3px solid #0f6e56", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      </div>
    </SuperviseurLayout>
  );

  if (error) return (
    <SuperviseurLayout>
      <div style={{ padding: "2rem", textAlign: "center", color: "#dc2626" }}>{error}</div>
    </SuperviseurLayout>
  );

  const { caisse, stats_portefeuille: sp, commerciaux, recapitulatif } = data;

  return (
    <SuperviseurLayout>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "1.5rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h1 style={{ fontSize: "18px", fontWeight: "500", color: "#111827", margin: 0 }}>
            Bonjour, {utilisateur?.prenom} 👋
          </h1>
          <p style={{ fontSize: "13px", color: "#6b7280", margin: "4px 0 0" }}>{today}</p>
        </div>
      </div>

      {/* Caisse superviseur */}
      <div style={{ background: "linear-gradient(135deg, #0f6e56, #1D9E75)", borderRadius: "16px", padding: "1.5rem", color: "white", marginBottom: "1.25rem" }}>
        <div style={{ fontSize: "13px", opacity: 0.85, marginBottom: "0.5rem" }}>Caisse du jour</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "1rem" }}>
          {[
            { label: "Caisse fixe", value: `${Number(caisse?.caisse_fixe || 0).toLocaleString("fr-FR")} F` },
            { label: "Solde début", value: `${Number(caisse?.solde_debut_journee || 0).toLocaleString("fr-FR")} F` },
            { label: "Sorties", value: `${Number(caisse?.sorties_credits || 0).toLocaleString("fr-FR")} F` },
            { label: "Entrées", value: `${Number(caisse?.entrees_credits || 0).toLocaleString("fr-FR")} F` },
            { label: "Transferts", value: `${Number(caisse?.retraits || 0).toLocaleString("fr-FR")} F` },
            { label: "Solde actuel", value: `${Number(caisse?.solde_actuel || 0).toLocaleString("fr-FR")} F` },
          ].map((item, i) => (
            <div key={i}>
              <div style={{ fontSize: "11px", opacity: 0.75, marginBottom: "2px" }}>{item.label}</div>
              <div style={{ fontSize: "16px", fontWeight: "500" }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Stats portefeuille */}
      <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", padding: "1.25rem", marginBottom: "1.25rem" }}>
        <div style={{ fontSize: "14px", fontWeight: "500", color: "#111827", marginBottom: "1rem" }}>Portefeuille</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "12px" }}>
          <StatCard label="Score moyen" value={sp?.score_moyen || 0} color="#0f6e56" />
          <StatCard label="Taux remb." value={`${sp?.taux_remboursement || 0}%`} color="#0f6e56" />
          <StatCard label="Total crédits" value={sp?.total_credits || 0} />
          <StatCard label="Remboursés" value={sp?.total_credits_rembourses || 0} color="#16a34a" />
          <StatCard label="Non remb." value={sp?.total_credits_non_rembourses || 0} color="#dc2626" />
          <StatCard label="En cours" value={sp?.credits_en_cours || 0} color="#185FA5" />
          <StatCard label="Clients bloqués" value={sp?.clients_bloques || 0} color="#dc2626" />
          <StatCard label="À risque" value={sp?.clients_a_risque || 0} color="#ea580c" />
        </div>
      </div>

      {/* Récapitulatif du jour */}
      <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", padding: "1.25rem", marginBottom: "1.25rem" }}>
        <div style={{ fontSize: "14px", fontWeight: "500", color: "#111827", marginBottom: "1rem" }}>Récapitulatif du jour</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "12px" }}>
          <StatCard label="Clients servis" value={recapitulatif?.total_clients || 0} />
          <StatCard label="Coupons vendus" value={recapitulatif?.total_coupons_vendus || 0} color="#0f6e56" />
          <StatCard label="Montant mobilisé" value={`${Number(recapitulatif?.total_montant_mobilise || 0).toLocaleString("fr-FR")} F`} color="#ea580c" />
          <StatCard label="Montant encaissé" value={`${Number(recapitulatif?.total_montant_encaisse || 0).toLocaleString("fr-FR")} F`} color="#0f6e56" />
          <StatCard label="Pénalités" value={`${Number(recapitulatif?.total_penalites_recuperees || 0).toLocaleString("fr-FR")} F`} color="#dc2626" />
        </div>
      </div>

      {/* Commerciaux */}
      <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", padding: "1.25rem" }}>
        <div style={{ fontSize: "14px", fontWeight: "500", color: "#111827", marginBottom: "1rem" }}>
          Mes commerciaux ({commerciaux?.length || 0})
        </div>
        {commerciaux?.length === 0 ? (
          <p style={{ color: "#6b7280", fontSize: "14px", textAlign: "center", padding: "1rem 0" }}>Aucun commercial affecté</p>
        ) : (
          commerciaux?.map((com, i) => (
            <div key={com.id} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "0.75rem 0", borderBottom: i < commerciaux.length - 1 ? "0.5px solid #f3f4f6" : "none" }}>
              <div style={{ width: "38px", height: "38px", borderRadius: "50%", backgroundColor: "#E1F5EE", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "500", color: "#0f6e56", flexShrink: 0 }}>
                {com.nom?.charAt(0)}{com.prenom?.charAt(0)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "13px", fontWeight: "500", color: "#111827" }}>{com.prenom} {com.nom}</div>
                <div style={{ fontSize: "11px", color: "#6b7280" }}>
                  Solde : {Number(com.solde_actuel || 0).toLocaleString("fr-FR")} F · {com.nb_coupons_vendus} coupon(s)
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "12px", color: com.clients_en_retard > 0 ? "#dc2626" : "#16a34a" }}>
                  {com.clients_en_retard} en retard
                </div>
                <div style={{ fontSize: "11px", color: "#9ca3af" }}>{com.nb_clients} client(s)</div>
              </div>
            </div>
          ))
        )}
      </div>
    </SuperviseurLayout>
  );
};

export default DashboardSuperviseur;